import React, { useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faSearch,
  faChevronLeft,
  faChevronRight,
  faShareAlt,
} from '@fortawesome/free-solid-svg-icons';
import MapView from './MapView';
import StatsFrame from './StatsFrame';
import NaughtyNiceSearch from './NaughtyNiceSearch';
import ChatWindow from './ChatWindow';
import './App.css';
import * as api from './api';

const socket = io();

function reformatSantaLocations(data) {
  const formatted = [];

  for (let i = 0; i < data.length - 1; i++) { // Stop before the last point
    const from = data[i];
    const to = data[i + 1]; // Use the next point without wrapping around

    formatted.push({
      from: {
        name: from.city,
        coordinates: [from.location.lon, from.location.lat],
      },
      to: {
        name: to.city,
        coordinates: [to.location.lon, to.location.lat],
      },
    });
  }

  return formatted;
}

function transformCityData(dataArr) {
  return dataArr.map((data) => {
    const [latitude, longitude] =
      data._source.Coordinates.split(',').map(Number);

    // Calculate nice_weight: Combine total_nice and nice_score
    const niceWeight =
      data._source.total_nice * (data._source.nice_score / 100);

    // Return the formatted object
    return {
      ADDRESS: data._source['Name'], // Use city 'Name' as the address
      COORDINATES: [longitude, latitude], // Coordinates with [longitude, latitude]
      nice_weight: niceWeight.toFixed(2), // Round to two decimal places
    };
  });
}

function App() {
  const [flightData, setFlightData] = useState(null);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  const luceniaServer = process.env.REACT_APP_LUCENIA_SERVER;
  const adminPassword = process.env.REACT_APP_LUCENIA_ADMIN_PASSWORD;
  const [santaLocations, setSantaLocations] = useState(null);
  const [naughtyNiceCities, setNaughtyNiceCities] = useState(null);

  // Left toolbar states
  const [showStatsFrame, setShowStatsFrame] = useState(false);
  const [showNaughtyNiceSearch, setShowNaughtyNiceSearch] = useState(false);
  const [showHeatmapLegend, setShowHeatmapLegend] = useState(false);

  // Mobile-specific active panel state
  const [activeMobilePanel, setActiveMobilePanel] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Chat state lifted to the parent
  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'System',
      text: "Want to chat with the North Pole? Enter a message and we'll see who's around to talk during this busy time! Who knows? You might get lucky and chat with Mrs. Claus.",
      system: true,
    },
  ]);
  const [chatInput, setChatInput] = useState('');
  const [chatIsTyping, setChatIsTyping] = useState(false);
  const [chatStatus, setChatStatus] = useState('');

  // GeoJSON state for the selected country
  const [countryGeoJson, setCountryGeoJson] = useState(null);
  const [heatmapData, setHeatmapData] = useState(null);

  // Map state: Zoom level and viewport bounds
  const [zoomLevel, setZoomLevel] = useState(2); // Default zoom level
  const [viewportBounds, setViewportBounds] = useState(null);

  // Calculate precision based on zoom level
  const calculatePrecision = (tileZoom) => {
    // Precision is directly the tile zoom level, clamped to [0, 29]
    const precision = Math.min(Math.max(tileZoom, 0), 29);
    return precision;
  };

  // Fetch data for heatmap based on current viewport and precision
  const fetchHeatmapData = async (boundary, precision) => {
    if (!boundary) {
      console.warn('No boundary provided for fetching heatmap data.');
      return;
    }

    const queryPayload = {
      size: 0,
      query: {
        geo_shape: {
          Coordinates: {
            relation: 'intersects',
            shape: boundary,
          },
        },
      },
      aggs: {
        heatmap: {
          geotile_grid: {
            field: 'Coordinates',
            precision: 20,
            size: 200000,
          },
          aggs: {
            nice_stats: { stats: { field: 'nice_score' } },
            top_city: {
              terms: {
                field: 'Name.keyword',
                size: 1,
              },
            },
          },
        },
      },
    };

    //console.log("Sending query to Lucenia:", JSON.stringify(queryPayload, null, 2));

    try {
      const response = await api.query({ index: 'cities', body: queryPayload });
      console.log('Response received from Lucenia:', response.data);

      const heatmapData = response.data.aggregations.heatmap.buckets.map(
        (bucket) => ({
          key: bucket.key,
          niceScore: bucket.nice_stats.avg,
          cityName: bucket.top_city?.buckets[0]?.key || 'Unknown',
        }),
      );

      if (heatmapData) {
        // console.log("Processed heatmap data:", heatmapData);
        setHeatmapData(heatmapData);
        setShowHeatmapLegend(!!heatmapData && heatmapData.length > 0); // Show legend if data exists
      } else {
        console.warn('No aggregation buckets found in the response.');
        setHeatmapData(null);
      }
    } catch (error) {
      console.error('Error fetching heatmap data:', error);
      setHeatmapData(null);
    }
  };

  // Calculate the bounding box dynamically
  const calculateBoundingBox = (coordinates) => {
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;

    const processPoints = (points) => {
      points.forEach(([lng, lat]) => {
        if (lng < minLng) minLng = lng;
        if (lat < minLat) minLat = lat;
        if (lng > maxLng) maxLng = lng;
        if (lat > maxLat) maxLat = lat;
      });
    };

    const processRings = (rings) => {
      rings.forEach((ring) => processPoints(ring));
    };

    // Check for multi-polygon structure
    if (Array.isArray(coordinates[0][0][0])) {
      // Multi-polygon
      coordinates.forEach((polygon) => processRings(polygon));
    } else if (Array.isArray(coordinates[0][0])) {
      // Single polygon with multiple rings
      processRings(coordinates);
    } else {
      // Single ring of points
      processPoints(coordinates);
    }

    return { minLng, minLat, maxLng, maxLat };
  };

  function HeatmapLegend() {
    return (
      <div className="heatmap-legend">
        <h4>Legend</h4>
        <ul>
          <li>
            <span
              className="color-box"
              style={{ backgroundColor: 'rgb(255, 0, 0)' }}
            ></span>{' '}
            Naughty
          </li>
          <li>
            <span
              className="color-box"
              style={{ backgroundColor: 'rgb(0, 100, 0)' }}
            ></span>{' '}
            Meh
          </li>
          <li>
            <span
              className="color-box"
              style={{ backgroundColor: 'rgb(0, 150, 0)' }}
            ></span>{' '}
            On the Fence
          </li>
          <li>
            <span
              className="color-box"
              style={{ backgroundColor: 'rgb(0, 200, 0)' }}
            ></span>{' '}
            Almost Nice
          </li>
          <li>
            <span
              className="color-box"
              style={{ backgroundColor: 'rgb(0, 255, 0)' }}
            ></span>{' '}
            Nice
          </li>
        </ul>
      </div>
    );
  }

  const handleCountrySelection = async (country) => {
    if (country.id === 'World') {
      setCountryGeoJson(null); // Clear the map
      return;
    }

    try {
      // Fetch country boundary from the countries index
      const countryResponse = await api.query({
        index: 'countries',
        body: {
          query: {
            match: {
              _id: country.id,
            },
          },
        },
      });

      // console.log('Searching for country:', country.id);

      // console.log('Country Response:', JSON.stringify(countryResponse.data, null, 2));

      const countryDoc = countryResponse.data.hits.hits[0];
      const countryBoundary = countryDoc._source.boundary;

      console.log('Country Boundary Coordinates:', countryBoundary.coordinates);
      console.log('Country Boundary Type:', countryBoundary.type);

      setCountryGeoJson(countryBoundary); // Update boundary for the map

      // Calculate bounding box
      const { minLng, minLat, maxLng, maxLat } = calculateBoundingBox(
        countryBoundary.coordinates,
      );
      const centerLng = (minLng + maxLng) / 2;
      const centerLat = (minLat + maxLat) / 2;

      // Pass the bounding box and center to MapView
      setFlyTo({
        longitude: centerLng,
        latitude: centerLat,
        bounds: [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
      });

      // Fetch the heatmap data
      if (countryBoundary) {
        const precision = calculatePrecision(zoomLevel);
        console.log('Fetching Heatmap Data for:', country.name);
        console.log('With Precision:', precision);
        fetchHeatmapData(countryBoundary, precision); // Fetch data for the selected country
      } else {
        console.warn('No boundary data available for the selected country.');
      }
    } catch (error) {
      console.error('Error fetching country boundary:', error);
    }
  };

  const handleMapChange = ({ zoom, bounds }) => {
    console.log('Zoom level changed. Fetching new heatmap...');
    console.log('Zoom Received in App.js:', zoom);
    console.log('Bounds Received in App.js:', bounds);
    // Pass the zoom directly to calculatePrecision and fetchHeatmapData
    const precision = calculatePrecision(zoom);
    console.log('Calculated Precision:', precision);

    if (countryGeoJson) {
      fetchHeatmapData(countryGeoJson, precision); // Pass the precision directly
    }
  };

  useEffect(() => {
    // socket.on('flightData', (data) => {
    //   console.log('Received flight data:', data);
    //   setFlightData(data);
    // });

    // return () => {
    //   socket.off('flightData');
    // };
    async function getStats() {
      try {
        const response = await api.stats();
        setFlightData(response);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    }
    getStats();
  }, []);

  //fetch santa locations
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.locations();

        if (response.data) {
          const formatted = reformatSantaLocations(response.data);
          setSantaLocations(formatted);

          // Automatically fly to Santa's last known location on page load
          if (formatted.length > 0) {
            setFlyTo({
                latitude: formatted[formatted.length - 1].to.coordinates[1],
                longitude: formatted[formatted.length - 1].to.coordinates[0],
                bounds: [
                  [
                    formatted[0].from.coordinates[0] - 0.5, // Add padding
                    formatted[0].from.coordinates[1] - 0.5,
                  ],
                  [
                    formatted[formatted.length - 1].to.coordinates[0] + 0.5,
                    formatted[formatted.length - 1].to.coordinates[1] + 0.5,
                  ],
                ],
              }
            );
          }
        }
      } catch (error) {
        // setSantaLocations([
        //   {
        //     from: {
        //       name: 'North Pole',
        //       coordinates: [135, 90],
        //     },
        //     to: {
        //       name: 'Toronto',
        //       coordinates: [-79.4, 43.7],
        //     },
        //   },
        //   {
        //     from: {
        //       name: 'Toronto',
        //       coordinates: [-79.4, 43.7],
        //     },
        //     to: {
        //       name: 'New York',
        //       coordinates: [-74, 40.7],
        //     },
        //   },
        //   {
        //     from: {
        //       name: 'New York',
        //       coordinates: [-74, 40.7],
        //     },
        //     to: {
        //       name: 'Chicago',
        //       coordinates: [-87.6, 41.9],
        //     },
        //   },
        //   {
        //     from: {
        //       name: 'Chicago',
        //       coordinates: [-87.6, 41.9],
        //     },
        //     to: {
        //       name: 'Los Angeles',
        //       coordinates: [-118.2, 34.1],
        //     },
        //   },
        //   {
        //     from: {
        //       name: 'Los Angeles',
        //       coordinates: [-118.2, 34.1],
        //     },
        //     to: {
        //       name: 'North Pole',
        //       coordinates: [135, 90],
        //     },
        //   },
        // ]);
        console.error('Error fetching locations:', error);
      }
    };

    fetchLocations();
  }, []);

  //fetch naughty and nice cities
  useEffect(() => {
    const fetchNaughtyNice = async () => {
      try {
        const santaLocations = await api.locations();
        console.log('Santa Locations:', santaLocations.data);
        if (santaLocations.data) {
          const formatted = transformCityData(santaLocations.data);
          setNaughtyNiceCities(formatted);
        } else {
          console.warn('Invalid data format:', santaLocations.data);
        }
      } catch (error) {
        console.error('Error fetching naughty nice data:', error);
      }
    };

    fetchNaughtyNice();
  }, []);

  // Countdown timer logic
  const [timeRemaining, setTimeRemaining] = useState('');
  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      const christmas = new Date(now.getFullYear(), 11, 25, 0, 0, 0);
      if (now > christmas) christmas.setFullYear(christmas.getFullYear() + 1);
      const diff = christmas - now;

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setTimeRemaining(
        `${String(days).padStart(2, '0')}d : ${String(hours).padStart(2, '0')}h : ${String(minutes).padStart(2, '0')}m : ${String(seconds).padStart(2, '0')}s`,
      );
    };

    const timer = setInterval(calculateTimeRemaining, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleShare = (platform) => {
    const shareText =
      "🎅 Experience the magic of @LuceniaIO's cutting-edge Geospatial Search 🌐🔎 with the Santa Tracker! 🎄\n\n #MerryChristmas #Christmas #Search #OpenSource\n\n";
    const shareUrl = window.location.href;
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent('https://santa.lucenia.dev');

    let shareLink = '';
    switch (platform) {
      case 'X':
        shareLink = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      case 'reddit':
        shareLink = `https://reddit.com/submit?url=${encodedUrl}&title=${encodedText}`;
        break;
      default:
        alert('Unsupported platform');
        return;
    }

    window.open(shareLink, '_blank');
  };


  useEffect(() => {
    const handleResize = () => {
      const isMobileWidth = window.innerWidth < 768;
      const isPortrait = window.matchMedia('(orientation: portrait)').matches;
      setIsMobile(isMobileWidth || isPortrait);
    };
    handleResize(); // ensure correct state on first load
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    }
  }, []);

  // Function to toggle panels
  const togglePanel = (panel) => {
    setActiveMobilePanel((prev) => {
      if (prev === panel) {
        const toolbarButtons = document.querySelectorAll('.mobile-toolbar button');
        toolbarButtons.forEach((btn) => {
          btn.classList.remove('active');
          btn.style.backgroundColor = '';
        });
        return null;
      }
      return panel;
    });
  };

  useEffect(() => {
    const toolbarButtons = document.querySelectorAll('.mobile-toolbar button');
    toolbarButtons.forEach((btn) => {
      const panel = btn.getAttribute('data-panel');
      if (panel === activeMobilePanel) {
        btn.classList.add('active');
        btn.style.backgroundColor = '#575d75';
      } else {
        btn.classList.remove('active');
        btn.style.backgroundColor = '';
      }
    });
  }, [activeMobilePanel]);

  return (
    <div className="App">
      {/* Header */}
      <header className="header">
        <div className="header-left">
          <a href="https://lucenia.io" target="_blank" rel="noopener noreferrer">
            <img src="logo.png" alt="Lucenia Logo" className="logo" />
          </a>
        </div>
        <div className="header-right">
          {!isMobile || (window.matchMedia('(orientation: landscape)').matches) ? (
            <div className="countdown">
              {timeRemaining.split(':').map((segment, index) => (
                <div key={index} className="flipping-clock-segment">
                  <div className="flip">{segment}</div>
                </div>
              ))}
            </div>
          ) : null}
          <div className="share-dropdown">
            <button className="share-button">
              <FontAwesomeIcon icon={faShareAlt} /> Share
            </button>
            <div className="dropdown-content" style={isMobile ? { marginTop: '62px' } : {}}>
              <button onClick={() => handleShare('X')}>X</button>
              <button onClick={() => handleShare('facebook')}>Facebook</button>
              <button onClick={() => handleShare('linkedin')}>LinkedIn</button>
              <button onClick={() => handleShare('reddit')}>Reddit</button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Map View */}
      <div className="map-container">
        <MapView
          data={flightData}
          countryGeoJson={countryGeoJson}
          heatmapData={heatmapData} // Ensure this line is present
          tileZoom={zoomLevel} // Pass zoomLevel (tile zoom) to MapView
          onZoomChange={setZoomLevel} // Capture zoom level
          onMapChange={handleMapChange}
          flyTo={flyTo} // Pass flyTo to MapView
          santaLocations={santaLocations}
          naughtyNiceCities={naughtyNiceCities}
        />
      </div>

      { isMobile ? (
          <>
            {/* Mobile Toolbar */}
            <div className="mobile-toolbar">
            <button 
              data-panel="stats"
              onClick={() => togglePanel('stats')} 
              className={activeMobilePanel === 'stats' ? 'active' : ''}
            >
                <FontAwesomeIcon icon={faChartBar} /> Stats
              </button>
            <button 
              data-panel="search"
              onClick={() => togglePanel('search')} 
              className={activeMobilePanel === 'search' ? 'active' : ''}
            >
              <FontAwesomeIcon icon={faSearch} /> Search
              </button>
            <button
              data-panel="chat"
              onClick={() => togglePanel('chat')} 
              className={activeMobilePanel === 'chat' ? 'active' : ''}
            >
                Chat
              </button>
            </div>

            {/* Mobile Panels */}
            <div className=".mobile-panels" style={{ marginTop: '100px' }}>
              {activeMobilePanel === 'stats' && (
                <div className="floating-panel">
                  <button className="close-button" onClick={() => setActiveMobilePanel(null)}>
                    X
                  </button>
                    <StatsFrame 
                    data={{
                      ...flightData,
                      coordinates: santaLocations?.[santaLocations.length - 1]?.to?.coordinates || null,
                      location: flightData.location, //santaLocations?.[santaLocations.length - 1]?.to?.name || 'Unknown',
                      bounds:
                      santaLocations?.length > 1
                        ? [
                            [
                              santaLocations[0].from.coordinates[0] - 0.5, // Expand bounds slightly
                              santaLocations[0].from.coordinates[1] - 0.5,
                           ],
                           [
                              santaLocations[santaLocations.length - 1].to.coordinates[0] + 0.5, // Expand bounds slightly
                              santaLocations[santaLocations.length - 1].to.coordinates[1] + 0.5,
                            ],
                          ]
                        : null,
                    }}
                    onFlyTo={(coordinates) => setFlyTo(coordinates)} />
                </div>
              )}
              {activeMobilePanel === 'search' && (
                <div className="floating-panel">
                  <button className="close-button" onClick={() => setActiveMobilePanel(null)}>
                    X
                  </button>
                  <NaughtyNiceSearch
                    setHeatmapData={setHeatmapData}
                    onCountrySelect={handleCountrySelection}
                  />
                </div>
              )}  
              {activeMobilePanel === 'chat' && (
                <div 
                  className="floating-panel chat-panel"
                  style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 140px)' }}
                >
                  <button className="close-button" onClick={() => setActiveMobilePanel(null)}>
                    X
                  </button>
                  <ChatWindow
                    messages={chatMessages}
                    setMessages={setChatMessages}
                    input={chatInput}
                    setInput={setChatInput}
                    isTyping={chatIsTyping}
                    setIsTyping={setChatIsTyping}
                    status={chatStatus}
                    setStatus={setChatStatus}
                  />
                </div>
              )}
            </div>
          </>
        ) : (
          <>
          {/* Left Toolbar */}
          <div className="left-toolbar">
            <button onClick={() => setShowStatsFrame(!showStatsFrame)}>
              <FontAwesomeIcon icon={faChartBar} />
            </button>
            <button
              onClick={() => setShowNaughtyNiceSearch(!showNaughtyNiceSearch)}
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
          </div>

          {/* Hovering Frames */}
          <div className="hovering-frames">
            {showStatsFrame && (
              <div className="hovering-frame stats-frame">
                <StatsFrame 
                  data={{
                    ...flightData,
                    coordinates: santaLocations?.[santaLocations.length - 1]?.to?.coordinates || null,
                    location: flightData.location, //santaLocations?.[santaLocations.length - 1]?.to?.name || 'Unknown',
                    bounds:
                    santaLocations?.length > 1
                      ? [
                          [
                            santaLocations[0].from.coordinates[0] - 0.5, // Expand bounds slightly
                            santaLocations[0].from.coordinates[1] - 0.5,
                          ],
                          [
                            santaLocations[santaLocations.length - 1].to.coordinates[0] + 0.5, // Expand bounds slightly
                            santaLocations[santaLocations.length - 1].to.coordinates[1] + 0.5,
                          ],
                        ]
                      : null,
                  }}
                  onFlyTo={(coordinates) => setFlyTo(coordinates)} />
              </div>
            )}
            {showNaughtyNiceSearch && (
              <div
                className="hovering-frame naughty-nice-frame"
                style={{ top: showStatsFrame ? '200px' : '0' }}
              >
                <NaughtyNiceSearch
                  setHeatmapData={setHeatmapData}
                  onCountrySelect={handleCountrySelection}
                  setShowHeatmapLegend={setShowHeatmapLegend} // Pass as a prop
                />
              </div>
            )}
            {showHeatmapLegend && (
              <div
                className={`heatmap-legend-frame ${
                  rightCollapsed ? 'right-collapsed' : ''
                }`}
              >
                <HeatmapLegend />
              </div>
            )}
          </div>

          {/* Right Panel */}
          <div className={`chat-window ${rightCollapsed ? 'collapsed' : ''}`}>
            <button
              className="collapse-button"
              onClick={() => setRightCollapsed(!rightCollapsed)}
            >
              <FontAwesomeIcon
                icon={rightCollapsed ? faChevronLeft : faChevronRight}
              />
            </button>
            {!rightCollapsed && (
              <ChatWindow
                messages={chatMessages}
                setMessages={setChatMessages}
                input={chatInput}
                setInput={setChatInput}
                isTyping={chatIsTyping}
                setIsTyping={setChatIsTyping}
                status={chatStatus}
                setStatus={setChatStatus}
              />
            )}
          </div>
          </>
        )
      }
    </div>
  );
}

export default App;
